import { useEffect, useState } from 'react';
import { channelsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Save, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import HelpButton from '@/components/HelpButton';

/**
 * A quién atiende el canal (`config.remitentes_admitidos`).
 *
 * **Por qué (2026-10-14).** Cualquier remitente se convertía en contacto y lo atendía un agente.
 * Diego pidió que cada canal declare a quién atiende y que el resto se descarte en silencio,
 * antes de crear el contacto y sin pasar por ningún modelo. Correo y Teams filtran por dominio o
 * correo; WhatsApp, por prefijo (país o característica) o número.
 *
 * Sin reglas, el canal atiende a todos: se dice en pantalla, para que no sea una sorpresa.
 *
 * **Excluidos (2026-10-14):** cuentas o números puntuales que el canal no atiende aunque su
 * dominio o su prefijo estén admitidos (por ejemplo una casilla que manda avisos). Ganan siempre.
 */
const LISTAS = {
  email: [
    { campo: 'dominios', rotulo: 'Dominios', ejemplo: 'pritty.com.ar\nfilomeni.com.ar' },
    { campo: 'correos', rotulo: 'Correos puntuales', ejemplo: 'proveedor@gmail.com' },
    { campo: 'correos_excluidos', rotulo: 'Correos excluidos (nunca se atienden)', ejemplo: 'comunicacionessistemas@pritty.com.ar', excluye: true },
  ],
  whatsapp: [
    { campo: 'prefijos', rotulo: 'Prefijos (país o característica)', ejemplo: '+54\n+54351' },
    { campo: 'numeros', rotulo: 'Números puntuales', ejemplo: '+54 351 683-6875' },
    { campo: 'numeros_excluidos', rotulo: 'Números excluidos (nunca se atienden)', ejemplo: '+54 351 555-1234', excluye: true },
  ],
};
LISTAS.teams = LISTAS.email;

const aTexto = (lista) => (Array.isArray(lista) ? lista.join('\n') : '');
const aLista = (texto) => (texto || '').split(/[\n,;]+/).map((v) => v.trim()).filter(Boolean);

export default function QuienAtiendeElCanal({ channel, onChannelUpdated }) {
  const tipo = channel?.channel_type;
  const listas = LISTAS[tipo];
  const guardadas = channel?.config?.remitentes_admitidos || {};
  const [textos, setTextos] = useState({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    const inicial = {};
    (listas || []).forEach(({ campo }) => { inicial[campo] = aTexto(guardadas[campo]); });
    setTextos(inicial);
    // Se resincroniza al cambiar de canal o cuando cambia lo guardado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel?.channel_id, JSON.stringify(guardadas)]);

  if (!listas) return null;

  const hayAdmision = listas.some(({ campo, excluye }) => !excluye && (guardadas[campo] || []).length > 0);
  const hayExclusion = listas.some(({ campo, excluye }) => excluye && (guardadas[campo] || []).length > 0);

  const guardar = async () => {
    setGuardando(true);
    try {
      const reglas = {};
      listas.forEach(({ campo }) => { reglas[campo] = aLista(textos[campo]); });
      const res = await channelsAPI.update(channel.channel_id, {
        config: { ...channel.config, remitentes_admitidos: reglas },
      });
      toast.success('Guardado: quién atiende este canal');
      onChannelUpdated?.(res.data);
    } catch (err) {
      const detalle = err.response?.data?.detail;
      toast.error(typeof detalle === 'string' ? detalle : 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="border border-zinc-200 rounded-sm p-3 space-y-3 bg-zinc-50/70" data-testid="quien-atiende-el-canal">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h5 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-600 flex items-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5" /> Quién atiende este canal
        </h5>
        <HelpButton section="channels_remitentes_admitidos" />
      </div>
      {hayAdmision ? (
        <p className="text-[10px] text-zinc-600">
          Solo entra quien coincide con alguna regla de admisión{hayExclusion ? ' y no está excluido' : ''}.
          El resto se descarta en silencio: no se le responde, no se crea el contacto y no llega a
          ningún agente. Queda en el log de diagnóstico.
        </p>
      ) : (
        <p className="text-[10px] text-amber-700 flex items-start gap-1" data-testid="canal-atiende-a-cualquiera">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          {hayExclusion
            ? 'Este canal atiende a cualquiera, menos a los excluidos.'
            : 'Este canal atiende a cualquiera. Cargá al menos una regla para limitarlo.'}
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {listas.map(({ campo, rotulo, ejemplo, excluye }) => (
          <div key={campo} className={excluye ? 'md:col-span-2' : ''}>
            <Label className={`text-[10px] uppercase ${excluye ? 'text-red-600' : 'text-zinc-500'}`}>{rotulo}</Label>
            <textarea
              value={textos[campo] || ''}
              onChange={(e) => setTextos((t) => ({ ...t, [campo]: e.target.value }))}
              className="w-full border border-brand-border rounded-sm p-2 text-[11px] font-mono bg-white min-h-[80px]"
              placeholder={ejemplo}
              data-testid={`remitentes-${campo}`}
            />
            <p className="text-[10px] text-zinc-500 mt-1">
              Uno por línea.{excluye ? ' Ganan sobre cualquier regla de admisión.' : ''}
            </p>
          </div>
        ))}
      </div>
      {tipo === 'whatsapp' && (
        <p className="text-[10px] text-zinc-500">
          Los prefijos alcanzan a los celulares con y sin el 9: <span className="font-mono">+54351</span> admite
          {' '}<span className="font-mono">+549351…</span>.
        </p>
      )}
      {tipo !== 'whatsapp' && (
        <p className="text-[10px] text-zinc-500">
          El dominio coincide exacto: <span className="font-mono">pritty.com.ar</span> no incluye
          {' '}<span className="font-mono">ventas.pritty.com.ar</span>.
        </p>
      )}
      <Button size="sm" onClick={guardar} disabled={guardando} data-testid="remitentes-guardar">
        {guardando ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
        Guardar
      </Button>
    </div>
  );
}
